import * as THREE from 'three';
import { Core } from './Core';
import { Timer } from './Timer';

export class TickService {
	core: Core;
	clock = new THREE.Clock();
	tickFrameCallbacks: TickCallback[] = [];
	tickSecCallbacks: TickCallback[] = [];
	tickHalfSecCallbacks: TickCallback[] = [];
	pauseCallbacks: TickCallback[] = [];
	resumeCallbacks: TickCallback[] = [];
	timersGame: Timer[] = [];
	timersUI: Timer[] = [];

	/**
	 * State
	 * */
	gameTime: number = 0;
	pausedTick = false;

	/**
	 * Constructor
	 * */
	constructor(core: Core) {
		this.core = core;
	}

	/**
	 * Starts the Tick Service
	 * */
	start() {
		this.clock.start();
		this.gameTime = 0;
		this.tick();
	}

	/**
	 * Starts the Tick Service
	 * */
	end() {
		this.pauseTick();
		this.tickFrameCallbacks = [];
		this.tickSecCallbacks = [];
		this.tickHalfSecCallbacks = [];
		this.timersGame = [];
		this.timersUI = [];
	}

	/**
	 * Animates and updates objects
	 * */
	tick = () => {

		// If running
		if (!this.pausedTick) {
			// Setup time properties
			const deltaTime = this.clock.getDelta();
			this.gameTime += deltaTime;
			const { isTickSecond, isTickHalfSecond } = this.checkTickFraction();

			// Tick Game Callback
			this.runTickAnimations(this.tickFrameCallbacks, this.gameTime, deltaTime);
			this.runGameTimers();
			if (isTickSecond) this.runTickAnimations(this.tickSecCallbacks, this.gameTime, deltaTime);
			if (isTickHalfSecond) this.runTickAnimations(this.tickHalfSecCallbacks, this.gameTime, deltaTime);
		}

		// Render
		this.core.renderer.render(this.core.threeScene, this.core.s('Camera').mainCamera.threeCamera);

		// Loop
		window.requestAnimationFrame(this.tick.bind(this));
	};

	/**
	 * Checks delta time for second fractions
	 * */
	checkTickFraction() {
		const accuracy = 0.01;

		const isTickSecond = Math.abs(this.gameTime - Math.round(this.gameTime)) <= accuracy;
		const isTickHalfSecond = Math.abs(this.gameTime - Math.round(this.gameTime * 2) / 2) <= accuracy;

		return { isTickSecond, isTickHalfSecond };
	}

	/**
	 * Runs animations on a given tick timeframe
	 * */
	runTickAnimations(callbacks: TickCallback[], elapsedTime: number, deltaTime: number) {
		callbacks.forEach((callback) => callback.callback.bind(this, { elapsedTime, deltaTime })());
	}

	/**
	 * Check and trigger Game Timers
	 * */
	runGameTimers() {
		this.timersGame.forEach(timer => {
			if (timer.completeGameTime <= this.gameTime) timer.trigger();
		});
	}

	/**
	 * Gets the current game time
	 * */
	getCurrentGameTime() {
		return this.gameTime;
	}

	/**
	 * Accepts a boolean to set the pause state, where true is paused.
	 */
	setTickPause(isPaused: boolean) {
		if (isPaused) this.pauseTick();
		else this.unpauseTick();
	}

	/**
	 * Pauses game objects
	 * */
	pauseTick() {
		this.clock.stop();
		this.pausedTick = true;

		this.pauseCallbacks.forEach(cbRegistration => cbRegistration.callback());
	}

	/**
	 * Unpauses game objects
	 * */
	unpauseTick() {
		this.clock.start();
		this.pausedTick = false;

		this.resumeCallbacks.forEach(cbRegistration => cbRegistration.callback());
	}

	/**
	 * Register a Tick callback
	 * */
	registerCallback = (callback: TickCallback, time: TickTimeTypes = TickTimeTypes.frame) => {
		let destinationArray = this.tickFrameCallbacks;

		if (time == TickTimeTypes.frame) destinationArray = this.tickFrameCallbacks;
		if (time == TickTimeTypes.second) destinationArray = this.tickSecCallbacks;
		if (time == TickTimeTypes.halfsecond) destinationArray = this.tickHalfSecCallbacks;

		// And Events
		if (time == TickTimeTypes.pause) destinationArray = this.pauseCallbacks; 
		if (time == TickTimeTypes.resume) destinationArray = this.resumeCallbacks; 

		destinationArray.push(callback);
	};

	/**
	 * Deregister a Tick callback
	 * */
	deregisterCallback(callbackName: string) {
		this.tickFrameCallbacks = this.tickFrameCallbacks.filter((thisCallback: TickCallback) => thisCallback.name !== callbackName);
		this.tickHalfSecCallbacks = this.tickHalfSecCallbacks.filter((thisCallback: TickCallback) => thisCallback.name !== callbackName);
		this.tickSecCallbacks = this.tickSecCallbacks.filter((thisCallback: TickCallback) => thisCallback.name !== callbackName);
	}

	/**
	 * Register a Timer which clears when run
	 * */
	registerTimer(timer: Timer, game = true) {
		if (game) this.timersGame.push(timer);
		else this.timersUI.push(timer);
	}

	/**
	 * Register a Timer which clears when run
	 * */
	deregisterTimer(removedTimer: Timer) {
		this.timersGame = this.timersGame.filter(timer => timer !== removedTimer);
	}

	/**
	 * Sets up Tick Debugs
	 */
	setupTickDebugs() {
		const sDebug = this.core.s('Debug');
		const folder = sDebug.lilGUI.addFolder('Playback').open(false);
		folder.add(this, 'pauseTick').name('Pause Tick');
		folder.add(this, 'unpauseTick').name('Unpause Tick');
	}
}

export class TickCallback {
	name: string;
	callback: Function;
	
	constructor(name: string, callback: Function) {
		this.name = name;
		this.callback = callback;
	}
}

export interface TickTimeProperties {
	elapsedTime: number;
	deltaTime: number;
}

export enum TickTimeTypes {
	frame,
	second,
	halfsecond,

	// And events
	pause,
	resume
}